import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DollarSign, TrendingUp, Users, Calendar } from 'lucide-react';

interface PaymentDashboardProps {
  organizationId?: string;
  clubId?: string;
}

export const PaymentDashboard: React.FC<PaymentDashboardProps> = ({
  organizationId,
  clubId
}) => {
  // Query payment statistics
  const { data: paymentStats, isLoading } = useQuery({
    queryKey: ['payment-stats', organizationId, clubId],
    queryFn: async () => {
      let query = supabase
        .from('payment_transactions')
        .select('gross_amount, platform_commission, club_commission, status, created_at');

      // Add filters based on context
      if (clubId) {
        query = query.eq('metadata->>club_id', clubId);
      } else if (organizationId) {
        query = query.eq('metadata->>organization_id', organizationId);
      }

      const { data, error } = await query;
      if (error) throw error;

      const totalRevenue = data?.reduce((sum, t) => sum + (t.gross_amount || 0), 0) || 0;
      const totalCommissions = data?.reduce((sum, t) => sum + (t.platform_commission || 0) + (t.club_commission || 0), 0) || 0;
      const successfulTransactions = data?.filter(t => t.status === 'succeeded').length || 0;
      const thisMonthTransactions = data?.filter(t => {
        const transactionDate = new Date(t.created_at);
        const now = new Date();
        return transactionDate.getMonth() === now.getMonth() && 
               transactionDate.getFullYear() === now.getFullYear();
      }).length || 0;

      return {
        totalRevenue,
        totalCommissions,
        successfulTransactions,
        thisMonthTransactions,
        totalTransactions: data?.length || 0
      };
    },
  });

  if (isLoading) {
    return <div>Loading payment statistics...</div>;
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Total Revenue
          </CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            ${((paymentStats?.totalRevenue || 0) / 100).toFixed(2)}
          </div>
          <p className="text-xs text-muted-foreground">
            All time revenue
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Commission Earned
          </CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            ${((paymentStats?.totalCommissions || 0) / 100).toFixed(2)}
          </div>
          <p className="text-xs text-muted-foreground">
            Total commission fees
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Successful Payments
          </CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {paymentStats?.successfulTransactions || 0}
          </div>
          <p className="text-xs text-muted-foreground">
            Out of {paymentStats?.totalTransactions || 0} total
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            This Month
          </CardTitle>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {paymentStats?.thisMonthTransactions || 0}
          </div>
          <p className="text-xs text-muted-foreground">
            Transactions this month
          </p>
        </CardContent>
      </Card>
    </div>
  );
};